import { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { createScrambleText } from "@/utils/scrambleText";

type LoginWordmarkProps = {
  label: string;
};

const SCRAMBLE_LOOP_DELAY_MS = 4200;

export function LoginWordmark({ label }: LoginWordmarkProps) {
  const reducedMotion = usePrefersReducedMotion();
  const nodeRef = useRef<HTMLParagraphElement>(null);
  const text = label.toUpperCase();

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    if (reducedMotion) {
      node.textContent = text;
      return;
    }

    let frame = 0;
    let rest = 0;
    let cancelled = false;

    const play = () => {
      const scramble = createScrambleText({ text });
      const startedAt = performance.now();
      const tick = (now: number) => {
        if (cancelled) {
          return;
        }
        const progress = Math.min(1, (now - startedAt) / scramble.durationMs);
        node.textContent = scramble.frame(progress);
        if (progress < 1) {
          frame = window.requestAnimationFrame(tick);
          return;
        }
        rest = window.setTimeout(play, SCRAMBLE_LOOP_DELAY_MS);
      };
      frame = window.requestAnimationFrame(tick);
    };

    play();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(rest);
      node.textContent = text;
    };
  }, [text, reducedMotion]);

  return <p ref={nodeRef} className="login-wordmark login-mono" aria-label={label} />;
}
